import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
  isThisYear,
  parseISO,
  compareDesc,
} from 'date-fns';
import {
  List,
  ListItemButton,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Collapse,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { getThemeStyles } from '../../themes/themeConfig';
import ChatListFooter from './ChatListFooter';
import { useTranslation } from 'react-i18next';
import { BrainGlyph, getAgtFromToolName } from '../messages/ToolIndicator';

const STORAGE_KEY = 'chatList_state';
const CHAT_TOOL_USAGE_KEY = 'chatToolUsage';

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// ===== Estilos
const StyledBox = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  '&::-webkit-scrollbar': {
    width: '6px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.divider,
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  ...getThemeStyles('ChatList', theme.palette.mode),
}));

const StyledList = styled(List)({
  padding: 0,
  width: '100%',
});

const StyledTypography = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flexGrow: 1,
    color: isWallpaperMode
      ? '#FFFFFF'
      : theme.palette.mode === 'dark'
        ? '#FFFFFF'
        : '#000000',
  };
});

const ChatTitleRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0
}));

const ToolIconWrapper = styled(Box)(() => ({
  position: 'relative',
  width: 20,
  height: 20,
  flexShrink: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

// Apple-style Tooltip
const AppleTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDark = theme.palette.mode === 'dark';

  return {
    '& .MuiTooltip-tooltip': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : isDark
        ? 'rgba(40, 40, 42, 0.95)'
        : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: isWallpaperMode
        ? '1px solid rgba(128, 128, 128, 0.3)'
        : isDark
        ? '1px solid rgba(255, 255, 255, 0.08)'
        : '1px solid rgba(0, 0, 0, 0.06)',
      borderRadius: '12px',
      padding: '12px',
      minWidth: '180px',
      maxWidth: '240px'
    },
    '& .MuiTooltip-arrow': {
      color: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : isDark
        ? 'rgba(40, 40, 42, 0.95)'
        : 'rgba(255, 255, 255, 0.95)',
      '&::before': {
        border: isWallpaperMode
          ? '1px solid rgba(128, 128, 128, 0.3)'
          : isDark
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(0, 0, 0, 0.06)'
      }
    }
  };
});

const TooltipTitle = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDark = theme.palette.mode === 'dark';

  return {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.6)'
      : isDark
      ? 'rgba(255, 255, 255, 0.5)'
      : 'rgba(0, 0, 0, 0.5)',
    marginBottom: '8px'
  };
});

const TooltipItem = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDark = theme.palette.mode === 'dark';

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.9)'
      : isDark
      ? 'rgba(255, 255, 255, 0.9)'
      : 'rgba(0, 0, 0, 0.85)',
    '&:not(:last-child)': {
      borderBottom: isWallpaperMode
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : isDark
        ? '1px solid rgba(255, 255, 255, 0.08)'
        : '1px solid rgba(0, 0, 0, 0.06)'
    }
  };
});

const ToolCountBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -2,
  right: -4,
  minWidth: 14,
  height: 14,
  padding: '0 4px',
  borderRadius: '9999px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 255, 255, 0.85)'
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#FFFFFF'),
  color: theme.palette.actualMode === 'wallpaper'
    ? '#000000'
    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
  fontSize: '10px',
  lineHeight: '14px',
  fontWeight: 600,
  textAlign: 'center',
  border: theme.palette.actualMode === 'wallpaper'
    ? '1px solid rgba(0, 0, 0, 0.35)'
    : (theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)')
}));


const getGradientMidColor = (colors = []) => {
  if (!Array.isArray(colors) || colors.length === 0) return '#9E9E9E';
  return colors[Math.floor(colors.length / 2)];
};

const getToolSvgColors = (toolName, toolInput, theme) => {
  const dataTheme = document.documentElement.getAttribute('data-theme');
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper' ||
    dataTheme === 'wallpaper' ||
    dataTheme === 'wallpaper-light' ||
    dataTheme === 'wallpaper-dark';
  const agtKey = getAgtFromToolName(toolName, toolInput);
  const agtGradient = {
    lifeos: [
      '#415B7E',
      '#2D476A',
      '#4A4F81',
      '#363B6D',
      '#A83836',
      '#BC4C4A',
      '#E78460',
      '#DC9844'
    ],
    fin: ['#FFFFFF', '#E6E6E6', '#CFCFCF'],
    fit: ['#FF8C00', '#FF6B00', '#FF5722'],
    health: ['#4CAF50', '#3d8b40', '#2e7031'],
    ups: ['#DC9844', '#C88430', '#B4701C'],
    default: isWallpaperMode
      ? ['#FFFFFF', '#FFFFFF', '#FFFFFF']
      : ['#9E9E9E', '#7A7A7A', '#5A5A5A']
  }[agtKey];
  const safeGradient = agtGradient || ['#9E9E9E', '#7A7A7A', '#5A5A5A'];
  const statusColor = getGradientMidColor(safeGradient);
  if (agtKey === 'lifeos') {
    return ['#2D476A'];
  }
  if (!isWallpaperMode && theme.palette.mode === 'light') {
    return [statusColor, statusColor, statusColor];
  }
  return safeGradient;
};

const StyledIconButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    ...getThemeStyles('AllChatsText', theme.palette.mode),
    color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
  };
});

// Apple-style section header
const AppleSectionHeader = styled(ListItemButton)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark' ? '#1C1C1E' : colors.background),
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? alpha('#FFFFFF', 0.05)
        : (theme.palette.mode === 'dark'
          ? alpha('#2C2C2E', 0.8)
          : alpha(colors.backgroundSecondary, 0.5)),
    },
    borderRadius: '8px',
    margin: '0 0px',
    color: isWallpaperMode
      ? '#FFFFFF'
      : theme.palette.mode === 'dark'
        ? '#FFFFFF'
        : '#000000',
    '& .MuiTypography-root': {
      color: isWallpaperMode ? '#FFFFFF' : 'inherit',
    },
    '& .MuiSvgIcon-root': {
      color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
    }
  };
});

// ===== Función para categorizar los chats según la fecha
const getChatCategory = (date, t) => {
  if (!date) {
    return t('categorizedChatList.noDate');
  }
  try {
    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) return t('categorizedChatList.today');
    if (isYesterday(parsedDate)) return t('categorizedChatList.yesterday');
    if (isThisWeek(parsedDate)) return t('categorizedChatList.thisWeek');
    if (isThisMonth(parsedDate)) return t('categorizedChatList.thisMonth');
    if (isThisYear(parsedDate)) {
      // Usar las traducciones para los meses
      const month = format(parsedDate, 'MMMM');
      const monthKey = month.toLowerCase();
      return t(`categorizedChatList.${monthKey}`);
    }
    return format(parsedDate, 'yyyy');
  } catch (error) {
    return t('categorizedChatList.invalidDate');
  }
};

const CategorizedChatList = ({
  chats,
  currentChatId,
  onSelectChat,
  onMenuOpen,
  hasNextPage,
  isNextPageLoading,
  onLoadMore,
  onSidebarClose,
  isMobile,
  selectedCategory,
  persistentState,
  onPersistentStateChange,
  onCreateNewChat,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [toolUsageMap, setToolUsageMap] = useState(() => {
    try {
      const raw = localStorage.getItem(CHAT_TOOL_USAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  });
  const getToolsForChat = useCallback((chatId) => {
    const entry = toolUsageMap[chatId];
    if (!entry) return [];
    if (Array.isArray(entry.tools)) return entry.tools;
    if (entry.toolName) {
      return [{ toolName: entry.toolName, toolInput: entry.toolInput }];
    }
    return [];
  }, [toolUsageMap]);

  const getAgtIconToolsForChat = useCallback((chatId) => {
    const tools = getToolsForChat(chatId);
    if (!tools.length) return [];
    const seen = new Set();
    const unique = [];
    tools.forEach((tool) => {
      const agtKey = getAgtFromToolName(tool.toolName, tool.toolInput);
      if (!seen.has(agtKey)) {
        seen.add(agtKey);
        unique.push({ agtKey, tool });
      }
    });
    return unique;
  }, [getToolsForChat]);

  const getAgtLabel = useCallback((agtKey) => {
    const labels = {
      fin: 'AGT Fin',
      fit: 'AGT Fit',
      health: 'AGT Health',
      ups: 'AGT Ups',
      lifeos: 'LifeOS',
      default: 'AGT'
    };
    return labels[agtKey] || 'AGT';
  }, []);
  // ===== Estado local para scroll, etc.
  const [state, setState] = useState(() => {
    const defaultState = {
      scrollPosition: 0,
      loadedChats: [],
      lastVisibleCategory: null,
      expandedSections: {
        chats: true,
        categories: {}
      }
    };
    
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return defaultState;
      
      const parsedState = JSON.parse(saved);
      
      // Asegurarnos de que la estructura es válida
      if (!parsedState.expandedSections) {
        parsedState.expandedSections = { chats: true, categories: {} };
      } else if (!parsedState.expandedSections.categories) {
        parsedState.expandedSections.categories = {};
      }
      
      return parsedState;
    } catch (error) {
      console.error('Error loading state:', error);
      return defaultState;
    }
  });

  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const categoriesRef = useRef(new Map());
  const isManualLoadRef = useRef(false);
  
  // UseInView para detectar cuando el final de la lista es visible
  const { ref: listEndRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  // === Filtramos los chats que tengan projectId para no mostrarlos aquí
  const relevantChats = useMemo(() => {
    return chats.filter((chat) => !chat.projectId);
  }, [chats]);

  // ===== Guardar en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving state:', error);
    }
  }, [state]);

  useEffect(() => {
    const handleToolUsageUpdate = (event) => {
      const { chatId, tools } = event.detail || {};
      if (!chatId || !Array.isArray(tools)) return;
      setToolUsageMap((prev) => ({
        ...prev,
        [chatId]: {
          ...prev[chatId],
          tools,
          updatedAt: Date.now()
        }
      }));
    };

    window.addEventListener('chatToolUsageUpdated', handleToolUsageUpdate);
    return () => {
      window.removeEventListener('chatToolUsageUpdated', handleToolUsageUpdate);
    };
  }, []);

  // ===== Restaurar posición de scroll
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = state.scrollPosition;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Actualizar posición de scroll en el estado
  const updateScrollPosition = useCallback((scrollTop) => {
    setState((prev) => ({
      ...prev,
      scrollPosition: scrollTop,
    }));
  }, []);


  // UseEffect para cargar más contenido cuando inView cambia
  useEffect(() => {
    if (inView && hasNextPage && !isNextPageLoading) {
      // Cargar más automáticamente si:
      // 1. Es una carga manual (usuario scrolleó hasta el final) O
      // 2. Hay menos de 20 chats (para asegurar que la lista inicial tenga contenido)
      const shouldLoadMore = isManualLoadRef.current || relevantChats.length < 20;

      if (shouldLoadMore) {
        onLoadMore();
        // Resetear el flag de carga manual
        isManualLoadRef.current = false;
      }
    }
  }, [inView, hasNextPage, isNextPageLoading, onLoadMore, relevantChats.length]);

  // ===== Manejar cambio de tamaño
  useEffect(() => {
    if (containerRef.current) {
      const handleResize = () => {
        updateScrollPosition(containerRef.current.scrollTop);
      };
      resizeObserverRef.current = new ResizeObserver(handleResize);
      resizeObserverRef.current.observe(containerRef.current);

      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }
  }, [updateScrollPosition]);

  // ===== Seleccionar chat
  const handleChatSelect = useCallback(
    (chatId) => {
      onSelectChat(chatId);
      if (isMobile) {
        onSidebarClose();
      }
    },
    [onSelectChat, isMobile, onSidebarClose]
  );

  // ===== Manejar scroll manual
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      updateScrollPosition(scrollTop);

      // Si el usuario está cerca del final (digamos 100px), considerar como carga manual
      if (scrollHeight - scrollTop - clientHeight < 100) {
        isManualLoadRef.current = true;
      }
    }
  }, [updateScrollPosition]);

  // ===== Agrupar los chats por categoría
  const categorizedChats = useMemo(() => {
    const categories = {};
    relevantChats.forEach((chat) => {
      const cat = getChatCategory(chat.updatedAt || chat.createdAt, t);
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(chat);
    });

    // Orden descendente dentro de cada categoría
    Object.values(categories).forEach((group) => {
      group.sort((a, b) => {
        const dateA = parseISO(a.updatedAt || a.createdAt);
        const dateB = parseISO(b.updatedAt || b.createdAt);
        return compareDesc(dateA, dateB);
      });
    });

    return categories;
  }, [relevantChats, t]);

  // Orden específico de categorías
  const categoryOrder = useMemo(() => [
    t('categorizedChatList.today'),
    t('categorizedChatList.yesterday'),
    t('categorizedChatList.thisWeek'),
    t('categorizedChatList.thisMonth'),
    t('categorizedChatList.january'),
    t('categorizedChatList.february'),
    t('categorizedChatList.march'),
    t('categorizedChatList.april'),
    t('categorizedChatList.may'),
    t('categorizedChatList.june'),
    t('categorizedChatList.july'),
    t('categorizedChatList.august'),
    t('categorizedChatList.september'),
    t('categorizedChatList.october'),
    t('categorizedChatList.november'),
    t('categorizedChatList.december'),
  ], [t]);

  // ===== Manejo de las secciones expandibles
  const toggleChatSection = useCallback(() => {
    setState(prev => ({
      ...prev,
      expandedSections: {
        ...prev.expandedSections,
        chats: !prev.expandedSections.chats
      }
    }));
  }, []);

  const toggleCategory = useCallback((category) => {
    setState(prev => ({
      ...prev,
      expandedSections: {
        ...prev.expandedSections,
        categories: {
          ...prev.expandedSections.categories,
          [category]: !prev.expandedSections.categories[category]
        }
      }
    }));
  }, []);

  // Asegurarse de que las categorías tengan un estado inicial
  useEffect(() => {
    const categoryNames = Object.keys(categorizedChats);
    const updatedCategories = { ...state.expandedSections.categories };
    let stateChanged = false;

    categoryNames.forEach(cat => {
      if (updatedCategories[cat] === undefined) {
        updatedCategories[cat] = true; // Por defecto, las categorías están expandidas
        stateChanged = true;
      }
    });

    if (stateChanged) {
      setState(prev => ({
        ...prev,
        expandedSections: {
          ...prev.expandedSections,
          categories: updatedCategories
        }
      }));
    }
  }, [categorizedChats, state.expandedSections.categories]);

  // Verificar si una categoría está expandida
  const isCategoryExpanded = useCallback((category) => {
    return state.expandedSections.categories[category] !== false;
  }, [state.expandedSections.categories]);
  
  // Contar el número total de chats
  const totalChats = relevantChats.length;

  return (
    <StyledBox 
      ref={containerRef} 
      onScroll={handleScroll}
    >
      <StyledList>
        {/* Sección principal de Chats con toggle */}
        <AppleSectionHeader 
          onClick={toggleChatSection}
          sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            paddingLeft: '8px', // Add less padding on the left side
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            flex: '1 1 auto',
            marginLeft: '6px', // Move the content slightly to the left
          }}>
            <ChevronRightIcon
              sx={{
                transform: state.expandedSections.chats ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.25s ease-in-out',
                color: theme => theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : theme.palette.text.secondary,
                mr: 1,
              }}
            />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: theme => theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
              }}
            >
              {t('categorizedChatList.chats')} {totalChats > 0 && `(${totalChats})`}
            </Typography>
          </Box>
        
          {/* This is a placeholder to force the space-between layout */}
          <Box sx={{ width: '24px' }}></Box>
        </AppleSectionHeader>

        {/* Contenido de Chats (colapsable) */}
        <Collapse in={state.expandedSections.chats} timeout={300}>
          {Object.entries(categorizedChats)
            .sort(([catA], [catB]) => {
              const indexA = categoryOrder.indexOf(catA);
              const indexB = categoryOrder.indexOf(catB);
              // Si ninguno está en categoryOrder, orden alfabético
              if (indexA === -1 && indexB === -1) return catA.localeCompare(catB);
              // Si solo uno está, el que sí está se pone primero
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            })
            .map(([category, categoryChats]) => (
              <Box
                key={category}
                ref={(element) => {
                  if (element) categoriesRef.current.set(category, element);
                }}
                data-category={category}
                sx={{ 
                  px: 1,
                  mt: 1,
                }}
              >
                {/* Cabecera de categoría con toggle */}
                <ListItemButton
                  onClick={() => toggleCategory(category)}
                  sx={(theme) => {
                    return {
                      py: 0.75,
                      px: 2,
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    };
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: theme => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : (theme.palette.mode === 'dark'
                          ? alpha('#FFFFFF', 0.85)
                          : alpha('#000000', 0.75)),
                    }}
                  >
                    {category} ({categoryChats.length})
                  </Typography>
                  <ChevronRightIcon
                    sx={{
                      fontSize: 20,
                      transform: isCategoryExpanded(category) ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.25s ease-in-out',
                      color: theme => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : theme.palette.text.secondary,
                    }}
                  />
                </ListItemButton>

                {/* Chats de esta categoría */}
                <Collapse in={isCategoryExpanded(category)} timeout={250}>
                  {categoryChats.map((chat) => (
                    <Box
                      key={chat.id}
                      sx={{
                        ml: 0,
                        mr: 0.5,
                        my: 0.5
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleChatSelect(chat.id)}
                        selected={currentChatId === chat.id}
                        sx={(theme) => {
                          const colors = navigationColors(theme);
                          return {
                            height: 36,
                            py: 0,
                            pl: 2,
                            pr: 1,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                            transition: 'all 0.15s ease-in-out',
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.actualMode === 'wallpaper'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : (theme.palette.mode === 'dark'
                                  ? '#2C2C2E'
                                  : colors.cardBackground),
                              '& .MuiTypography-root': {
                                color: theme.palette.actualMode === 'wallpaper'
                                  ? '#FFFFFF'
                                  : colors.accent,
                                fontWeight: 500,
                              },
                            },
                            '&:hover': {
                              backgroundColor: alpha(colors.backgroundSecondary, 0.7),
                              borderRadius: '8px',
                            },
                          };
                        }}
                      >
                        <StyledTypography
                          noWrap
                          sx={{
                            fontSize: '14px',
                            letterSpacing: '-0.1px',
                            flex: 1,
                            minWidth: 0
                          }}
                        >
                          <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {chat.title || 'New chat'}
                          </Box>
                        </StyledTypography>

                        {getAgtIconToolsForChat(chat.id).length > 0 && (
                          <AppleTooltip
                            placement="top"
                            arrow
                            enterDelay={300}
                            leaveDelay={200}
                            enterTouchDelay={50}
                            leaveTouchDelay={1500}
                            disableFocusListener
                            disableHoverListener={false}
                            disableTouchListener={false}
                            title={
                              <Box>
                                <TooltipTitle>
                                  {t('categorizedChatList.agtsUsed', 'AGTs Used')}
                                </TooltipTitle>
                                {getAgtIconToolsForChat(chat.id).map((entry, index) => (
                                  <TooltipItem key={`${chat.id}-tooltip-${entry.agtKey}-${index}`}>
                                    <Box sx={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                      <BrainGlyph
                                        colors={getToolSvgColors(entry.tool.toolName, entry.tool.toolInput, theme)}
                                        gradientId={`chat-tool-tooltip-${chat.id}-${entry.agtKey}-${index}`}
                                      />
                                    </Box>
                                    <Box component="span">{getAgtLabel(entry.agtKey)}</Box>
                                  </TooltipItem>
                                ))}
                              </Box>
                            }
                          >
                            <ToolIconWrapper
                              onClick={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onTouchStart={(event) => {
                                event.stopPropagation();
                              }}
                              onTouchEnd={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                              }}
                            >
                              <BrainGlyph
                                colors={getToolSvgColors(
                                  getAgtIconToolsForChat(chat.id)[0].tool.toolName,
                                  getAgtIconToolsForChat(chat.id)[0].tool.toolInput,
                                  theme
                                )}
                                gradientId={`chat-tool-${chat.id}-primary`}
                              />
                              {getAgtIconToolsForChat(chat.id).length > 1 && (
                                <ToolCountBadge>
                                  +{getAgtIconToolsForChat(chat.id).length - 1}
                                </ToolCountBadge>
                              )}
                            </ToolIconWrapper>
                          </AppleTooltip>
                        )}

                        <StyledIconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            onMenuOpen(event, chat.id);
                          }}
                          sx={{
                            opacity: 0.8,
                            '&:hover': {
                              opacity: 1,
                              backgroundColor: 'transparent',
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </StyledIconButton>
                      </ListItemButton>
                    </Box>
                  ))}
                </Collapse>
              </Box>
            ))}

          <Box
            ref={listEndRef}
            sx={{
              height: '40px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 2
            }}
          >
            {isNextPageLoading && <CircularProgress size={24} />}
          </Box>
        </Collapse>
      </StyledList>

      <ChatListFooter
        category={selectedCategory}
        chatsCount={relevantChats.length}
        hasNextPage={hasNextPage}
        isNextPageLoading={isNextPageLoading}
        onLoadMore={onLoadMore}
        onCreateNewChat={onCreateNewChat}
      />
    </StyledBox>
  );
};

export default CategorizedChatList;
